import type { BankStatusFilter } from './bankShared.ts';
import type { ReactNode } from 'react';

/** Status sub-nav + search + tag chips, above the category rail. Presentational:
 *  the library surface owns every value and setter. */
type BankToolbarProps = {
  statusFilter: BankStatusFilter;
  onStatusFilter: (s: BankStatusFilter) => void;
  counts: { active: number; archived: number; all: number };
  tags: string[];
  tagFilter?: string | null;
  onTagFilter: (tag: string | null) => void;
};

export default function BankToolbar({
  statusFilter,
  onStatusFilter,
  counts,
  tags,
  tagFilter,
  onTagFilter,
}: BankToolbarProps) {
  return (
    <div className="bank__toolbar">
      {/* Status is one axis with one answer, so it is the shared `.view-nav`
          sub-nav the decks and library pages use, on its own row above the
          search — not chips, which read as combinable toggles. The TAG chips
          below stay chips: they filter by a value, not by a view. */}
      <nav className="view-nav bank__status" role="tablist" aria-label="Filter by status">
        <StatusTab active={statusFilter === 'active'} count={counts.active} onClick={() => onStatusFilter('active')}>
          Active
        </StatusTab>
        <StatusTab
          active={statusFilter === 'archived'}
          count={counts.archived}
          onClick={() => onStatusFilter('archived')}
        >
          Archived
        </StatusTab>
        <StatusTab active={statusFilter === 'all'} count={counts.all} onClick={() => onStatusFilter('all')}>
          All
        </StatusTab>
      </nav>

      {tags.length > 0 && (
        <div className="tag-filter" role="group" aria-label="Filter by tag">
          {tags.map((t) => (
            <button
              key={t}
              type="button"
              className={'tag-chip tag-chip--filter' + (tagFilter === t ? ' is-active' : '')}
              aria-pressed={tagFilter === t}
              onClick={() => onTagFilter(tagFilter === t ? null : t)}
            >
              {t}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusTab({
  active,
  count,
  onClick,
  children,
}: {
  active: boolean;
  count: number;
  onClick: () => void;
  children?: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      className={'view-nav__item' + (active ? ' is-active' : '')}
      aria-selected={active}
      onClick={onClick}
    >
      {children} <span className="view-nav__count">{count}</span>
    </button>
  );
}
